import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CassError } from '@core/models/cass-error';
import { UserProfile } from '@core/models/user-profile';
import { UserProfilesService } from '@core/services/user-profiles.service';
import { TranslateService } from '@ngx-translate/core';
import { LoadingOverlayService } from '@shared/services/loading-overlay.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { SubSink } from 'subsink';

@Component({
	selector: 'cass-user-profile-page',
	templateUrl: './user-profile-page.component.html',
	styleUrls: ['./user-profile-page.component.css']
})
export class UserProfilePageComponent implements OnInit, OnDestroy {

	userName: string = "";
	userEmail: string = "";

	isChangePasswordPanelShown = false;
	changePasswordForm: FormGroup;
	newPasswordControl: FormControl;
	confirmPasswordControl: FormControl;

	protected isPasswordShown = false;

	private subs = new SubSink();
	constructor (
		private _alertService: UserAlertService,
		private _loadingOverlayService: LoadingOverlayService,
		private _profileService: UserProfilesService,
		private _translate: TranslateService
	) {
		this.newPasswordControl = new FormControl('', [Validators.required, Validators.minLength(8)]);
		this.confirmPasswordControl = new FormControl('', { validators: [Validators.required, Validators.minLength(8), this.passwordsMatchValidator.bind(this)], updateOn: 'change' });
		this.changePasswordForm = new FormGroup(
			{
				newPassword: this.newPasswordControl,
				confirmPassword: this.confirmPasswordControl
			}
		);
	}

	ngOnInit() {
		this.subs.add(
			this._profileService.userProfile$.subscribe((userProfile: UserProfile | undefined) => {
				if (userProfile) {
					this.initComponent(userProfile);
				}
			}));
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
	}

	private initComponent(userProfile: UserProfile): void {
		this.userName = ("" + userProfile.firstName || "" + " " + userProfile.lastName || "").trim();
		this.userEmail = userProfile.email || "";
	}

	displayChangePasswordPanel(): void {
		if (!this.isChangePasswordPanelShown) {
			this.isChangePasswordPanelShown = true;
			this.changePasswordForm.reset();
		}
	}

	private passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
		let pass = this.newPasswordControl?.value;
		let confirmPass = this.confirmPasswordControl?.value;

		if (pass && confirmPass && pass !== confirmPass) {
			const validationError: ValidationErrors = { passwordsDoNotMatch: true };
			this.confirmPasswordControl?.markAsTouched();
			return validationError;
		}

		return null;
	}

	submitPasswordChange(): void {
		if (!this.changePasswordForm.valid) {
			this.changePasswordForm.markAllAsTouched();
			return;
		}

		const profile = new UserProfile();
		profile.password = this.newPasswordControl?.value;

		this._loadingOverlayService.showIndeterminateHorizontalProgressIndicator();
		this._profileService.updateUserProfile(profile).subscribe({
			next: (updatedProfile: UserProfile) => {
				this._loadingOverlayService.hide();
				this.subs.add(
					this._translate.get("PROFILE.MSG_PASSWORD_UPDATED").subscribe({
						next: (msg: string) => {
							this.isChangePasswordPanelShown = false;
							this._alertService.showSuccessSnackbar(msg);
						}
					}));
			},
			error: (error: CassError) => {
				this._loadingOverlayService.hide();
				this._alertService.errorByCassError(error);
			}
		});
	}

	cancelPasswordChange(): void {
		this.isChangePasswordPanelShown = false;
	}
}
